import { ReactElement, useCallback } from 'react';
import { DropdownItemProps } from '@cas/components/inputs/dropdown-items/DropdownItems';
import { useMemo } from 'react';
import ColumnDropdown from '@customer/components/inputs/column-dropdown/ColumnDropdown';
import { useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@customer/store/hooks';
import useRawTrackColumnDefs, { StrictColDef } from '../raw-tracks/useRawTrackColumnDefs';
import { RawTrack } from '@customer/store/apis/investigationsApi';
import { RawTrackColumns, setRawTrackCols } from '@customer/store/slices/investigationGuiSlice';

const useRawTrackColumnsOptions = (): DropdownItemProps<RawTrackColumns>[] => {
  const columnDefs = useRawTrackColumnDefs();

  return useMemo(() => {
    return columnDefs.map(({ colId, headerName }: StrictColDef<RawTrack, RawTrackColumns>) => {
      return {
        key: colId,
        value: colId,
        stringValue: colId,
        label: headerName,
      };
    });
  }, [columnDefs]);
};

// Retrieve the list of visible columns from the investigationGui slice.
const useRawTrackColumnsValue = (): RawTrackColumns[] => {
  const rawTrackCols = useAppSelector(state => state.investigationGui.rawTrackCols);
  return rawTrackCols;
};

export const RawTracksColumnDropdown = (): ReactElement => {
  const dispatch = useAppDispatch();
  const { investigationId } = useParams();
  const siteId = useAppSelector(state => state.auth.siteId);

  const callback = useCallback(
    (rawTrackCols: RawTrackColumns[]) => {
      if (typeof investigationId !== 'undefined' && typeof siteId !== 'undefined') {
        // Ensure that at least one column is always shown (Time).
        const newValue = rawTrackCols.length ? rawTrackCols : [RawTrackColumns.Time];
        dispatch(setRawTrackCols(newValue));
      }
    },
    [dispatch, investigationId, siteId],
  );

  return (
    <ColumnDropdown<RawTrackColumns>
      uid="raw-tracks-columns-dropdown"
      useValueHook={useRawTrackColumnsValue}
      useItemsHook={useRawTrackColumnsOptions}
      callback={callback}
    />
  );
};

export default RawTracksColumnDropdown;
